import { Graphics, TextStyle, FillGradient } from 'pixi.js';

/**
 * Pixi-side mirror of the shared "Fusion" design system
 * (`src/ui/styles/designSystem.ts`). Use these tokens + helpers for every
 * in-game HUD element so the canvas chrome matches the HTML surfaces:
 * GunBound glossy beveled blue/gold panels + Worms chunky display type.
 *
 * Keep the palette in lockstep with the CSS custom properties.
 */
export const COLORS = {
  // Surface / chrome
  bg: 0x0c1830,
  panelTop: 0x21407a,
  panelBottom: 0x0e2046,
  bevelLight: 0x5288d6,
  bevelDark: 0x060f22,
  stroke: 0x2a4d86,

  // Accents
  gold: 0xffc63b,
  goldDeep: 0xe89a1c,
  go: 0x46c84a,
  goDeep: 0x2f9b35,
  danger: 0xec4438,
  dangerDeep: 0xb32a20,
  cyan: 0x38c6e0,
  blue: 0x3a7bd5,
  purple: 0xa463d8,

  // Text
  text: 0xffffff,
  textDim: 0xaebbd6,
  ink: 0x08152e,

  // LCD readouts (turn timer)
  lcdRed: 0xff463a,
  lcdBg: 0x1c0808,
  lcdDim: 0x4a1714,

  black: 0x000000,
  white: 0xffffff,
} as const;

export const FONTS = {
  display: 'Lilita One, system-ui, "Arial Black", sans-serif',
  ui: 'Fredoka, system-ui, -apple-system, sans-serif',
} as const;

export interface PanelOptions {
  radius?: number;
  /** Accent ring color (default subtle blue stroke). */
  accent?: number;
  /** Fill gradient endpoints (defaults to the standard blue chrome). */
  top?: number;
  bottom?: number;
  /** Outer stroke width. */
  strokeWidth?: number;
  alpha?: number;
}

/**
 * Draw a beveled glossy chrome panel into an existing Graphics object:
 * a vertical gradient body, a bright top sheen, a dark inner base shadow,
 * and an accent ring. The single reusable "frame" that unifies the HUD.
 */
export function drawPanel(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  opts: PanelOptions = {},
): void {
  const {
    radius = 12,
    accent = COLORS.bevelLight,
    top = COLORS.panelTop,
    bottom = COLORS.panelBottom,
    strokeWidth = 2,
    alpha = 0.96,
  } = opts;

  const grad = new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    textureSpace: 'local',
    colorStops: [
      { offset: 0, color: top },
      { offset: 1, color: bottom },
    ],
  });

  // Body
  g.roundRect(x, y, w, h, radius).fill({ fill: grad, alpha });
  // Top sheen (bright inner highlight)
  g.roundRect(x + 2, y + 2, w - 4, Math.max(2, h * 0.34), radius - 2).fill({
    color: COLORS.white,
    alpha: 0.1,
  });
  // Accent ring
  g.roundRect(x, y, w, h, radius).stroke({ width: strokeWidth, color: accent, alpha: 0.85 });
  // Crisp outer edge
  g.roundRect(x - 0.5, y - 0.5, w + 1, h + 1, radius + 1).stroke({
    width: 1,
    color: COLORS.bevelDark,
    alpha: 0.8,
  });
}

export interface BeveledButtonColors {
  top: number;
  bottom: number;
  edge: number;
}

export const BUTTON_THEMES: Record<string, BeveledButtonColors> = {
  gold: { top: 0xffd860, bottom: 0xe89a1c, edge: 0xa96a0e },
  green: { top: 0x5fd463, bottom: 0x2f9b35, edge: 0x1d6b22 },
  blue: { top: 0x5b9be8, bottom: 0x2f63b8, edge: 0x1d447f },
  danger: { top: 0xf4675c, bottom: 0xc4382c, edge: 0x8c241b },
  slate: { top: 0x33446b, bottom: 0x212f4f, edge: 0x141d33 },
};

/** Draw a toy-like 3D beveled button face (use `pressed` for the active state). */
export function drawBeveledButton(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  theme: BeveledButtonColors,
  radius = 12,
  pressed = false,
): void {
  const drop = pressed ? 1 : 4;
  const grad = new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    textureSpace: 'local',
    colorStops: [
      { offset: 0, color: theme.top },
      { offset: 1, color: theme.bottom },
    ],
  });
  // 3D base edge
  g.roundRect(x, y + drop, w, h, radius).fill({ color: theme.edge });
  // Face
  g.roundRect(x, y, w, h, radius).fill({ fill: grad });
  // Top sheen
  g.roundRect(x + 2, y + 2, w - 4, h * 0.4, radius - 2).fill({ color: COLORS.white, alpha: 0.4 });
}

export interface TextStyleOptions {
  size?: number;
  color?: number;
  display?: boolean;
  weight?: 'normal' | 'bold' | '500' | '600' | '700';
  outline?: number;
  outlineColor?: number;
  shadow?: boolean;
  wordWrapWidth?: number;
  align?: 'left' | 'center' | 'right';
  letterSpacing?: number;
}

/**
 * HUD text style with a dark outline + soft drop shadow so labels stay
 * legible over any terrain/sky (game-ui-design: never HUD text without
 * outline/shadow). Defaults to the chunky UI font at a >=14px size.
 */
export function textStyle(opts: TextStyleOptions = {}): TextStyle {
  const {
    size = 16,
    color = COLORS.text,
    display = false,
    weight = display ? 'normal' : '600',
    outline = display ? 3 : 3,
    outlineColor = 0x0a1426,
    shadow = true,
    wordWrapWidth,
    align = 'left',
    letterSpacing = 0,
  } = opts;

  return new TextStyle({
    fontFamily: display ? FONTS.display : FONTS.ui,
    fontSize: size,
    fontWeight: weight,
    fill: color,
    align,
    letterSpacing,
    stroke: { color: outlineColor, width: outline, join: 'round' },
    ...(shadow
      ? {
          dropShadow: {
            color: 0x000000,
            alpha: 0.45,
            blur: 2,
            angle: Math.PI / 2,
            distance: 2,
          },
        }
      : {}),
    ...(wordWrapWidth ? { wordWrap: true, wordWrapWidth } : {}),
  });
}
